<template>
  <v-app id="lex-web" :class="{ 'ui-minimized': isUiMinimized }">
    <!-- Floating chat button removed for full screen mode -->

    <!-- Chat container (full screen) -->
    <div class="chat-container chat-fullscreen">
      <!-- Fixed header -->
      <div class="chat-header">
        <div class="header-left">
          <div class="menu-dropdown">
            <button class="menu-btn" @click="toggleMenu" title="Menu">
              <span class="menu-icon">☰</span>
            </button>
            <div class="dropdown-content" v-if="isMenuOpen">
              <div class="dropdown-item" @click="clearChat">
                <span class="dropdown-icon">🗑️</span>
                <span class="dropdown-text">Cancella chat</span>
              </div>
            </div>
          </div>
          
          <!-- Previous button - only visible after 3rd message appears -->
          <button 
            class="prev-btn" 
            @click="goToPrevious" 
            title="Previous"
            v-if="$store.state.messages.length >= 3"
          >
            <span class="prev-icon">←</span>
          </button>
          
          <h1 class="chat-title">Amazon Device Repair</h1>
        </div>
        
        <div class="chat-controls">
          <!-- End Chat Button - only show after name is provided -->
          <button 
            class="end-chat-btn" 
            @click="handleEndLiveChat" 
            title="Termina"
            v-if="shouldShowEndChatButton"
          >
            <span class="end-chat-text">Termina</span>
            <span class="end-chat-icon">📞</span>
          </button>
        </div>
      </div>
      
      <!-- Scrollable content area -->
      <div class="chat-content-area">
        <v-main
          class="chat-main"
          :class="{ 'main-hidden': isUiMinimized }"
        >
          <v-container
            class="message-list-container"
            :class="[`toolbar-height-${toolbarHeightClassSuffix}`, {'fullscreen-mode': isMaximized}]"
            fluid
          >
            <message-list
              class="concierge-messages"
              :class="{ 'messages-hidden': isUiMinimized }"
            ></message-list>
          </v-container>
        </v-main>

        <input-container
          ref="InputContainer"
          :class="{ 'input-hidden': isUiMinimized || hasButtons, 'input-visible': !isUiMinimized && !hasButtons }"
          :text-input-placeholder="textInputPlaceholder"
          :initial-speech-instruction="initialSpeechInstruction"
          class="concierge-input"
        ></input-container>
        <div
          v-if="isSFXOn"
          id="sound"
          aria-hidden="true"
        />
      </div>
    </div>
  </v-app>
</template>

<script>
/*
Copyright 2017-2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Amazon Software License (the "License"). You may not use this file
except in compliance with the License. A copy of the License is located at

http://aws.amazon.com/asl/

or in the "license" file accompanying this file. This file is distributed on an "AS IS"
BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. See the
License for the specific language governing permissions and limitations under the License.
*/

/* eslint no-console: ["error", { allow: ["warn", "error", "info"] }] */

import MinButton from '@/components/MinButton';
import ToolbarContainer from '@/components/ToolbarContainer';
import MessageList from '@/components/MessageList';
import InputContainer from '@/components/InputContainer';
import LexRuntime from 'aws-sdk/clients/lexruntime';
import LexRuntimeV2 from 'aws-sdk/clients/lexruntimev2';

import { Config as AWSConfig, CognitoIdentityCredentials }
  from 'aws-sdk/global';

export default {
  name: 'lex-web',
  data() {
    return {
      toolbarHeightClassSuffix: 'md',
      isMaximized: false,
      isMenuOpen: false,
      previousConversationState: null, // Store previous conversation state
      conversationCount: 0, // Track conversation count
    };
  },
  components: {
    MinButton,
    ToolbarContainer,
    MessageList,
    InputContainer,
  },
  computed: {
    shouldShowEndChatButton() {
      // Check if the name question has been asked and answered
      const messages = this.$store.state.messages;
      let nameQuestionAsked = false;
      let nameProvided = false;
      let chatEnded = false;
      
      for (let i = 0; i < messages.length; i++) {
        const message = messages[i];
        
        // Check if bot asked for name
        if (message.type === 'bot' && message.text && 
            message.text.includes('Prima di iniziare una chat dal vivo, per favore dimmi il tuo nome?')) {
          nameQuestionAsked = true;
        }
        
        // Check if user provided name after the question
        if (nameQuestionAsked && message.type === 'human' && message.text && message.text.trim().length > 0) {
          nameProvided = true;
        }
        
        // Check if chat ended message appears
        if (message.type === 'bot' && message.text && 
            message.text.includes('La chat è terminata.')) {
          chatEnded = true;
        }
      }
      
      console.log('End Chat Button Logic:', { nameQuestionAsked, nameProvided, chatEnded, shouldShow: nameQuestionAsked && nameProvided && !chatEnded });
      return nameQuestionAsked && nameProvided && !chatEnded;
    },
    initialSpeechInstruction() {
      return this.$store.state.config.lex.initialSpeechInstruction;
    },
    textInputPlaceholder() {
      return this.$store.state.config.ui.textInputPlaceholder;
    },
    toolbarColor() {
      return this.$store.state.config.ui.toolbarColor;
    },
    toolbarTitle() {
      return this.$store.state.config.ui.toolbarTitle;
    },
    toolbarLogo() {
      return this.$store.state.config.ui.toolbarLogo;
    },
    toolbarStartLiveChatLabel() {
      return this.$store.state.config.ui.toolbarStartLiveChatLabel;
    },
    toolbarStartLiveChatIcon() {
      return this.$store.state.config.ui.toolbarStartLiveChatIcon;
    },
    toolbarEndLiveChatLabel() {
      return this.$store.state.config.ui.toolbarEndLiveChatLabel;
    },
    toolbarEndLiveChatIcon() {
      return this.$store.state.config.ui.toolbarEndLiveChatIcon;
    },
    isSFXOn() {
      return this.$store.state.isSFXOn;
    },
    isUiMinimized() {
      return this.$store.state.isUiMinimized;
    },
    hasButtons() {
      return this.$store.state.hasButtons;
    },
    lexState() {
      return this.$store.state.lex;
    },
    isMobile() {
      const mobileResolution = 900;
      return (
        'navigator' in window && navigator.maxTouchPoints > 0 &&
        'screen' in window &&
        (window.screen.height < mobileResolution ||
          window.screen.width < mobileResolution)
      );
    },
    messages() {
      return this.$store.state.messages;
    },
  },
  watch: {
    // emit lex state on changes
    lexState() {
      this.$emit('updateLexState', this.lexState);
      this.setFocusIfEnabled();
    },
    isUiMinimized(newVal) {
      // Force UI refresh when minimized state changes
      this.$nextTick(() => {
        // Trigger a DOM update
        window.dispatchEvent(new Event('resize'));
        
        if (!newVal && this.$refs.InputContainer) {
          // If maximizing, focus the input field
          this.$refs.InputContainer.setInputTextFieldFocus();
        }
      });
    },
    messages: {
      handler(newMessages) {
        // Update conversation count based on message patterns
        this.updateConversationCount(newMessages);
      },
      deep: true
    },
  },
  created() {
    // override default vuetify vertical overflow on non-mobile devices
    // hide vertical scrollbars
    if (!this.isMobile) {
      document.documentElement.style.overflowY = 'hidden';
    }

    this.initConfig()
      .then(() => {
        // Ensure awsConfig and credentials exist before proceeding
        if (!this.$lexWebUi.awsConfig) {
          this.$lexWebUi.awsConfig = { credentials: null };
        }
        
        return Promise.all([
          this.$store.dispatch(
            'initCredentials',
            this.$lexWebUi.awsConfig.credentials || null,
          ),
          this.$store.dispatch('initRecorder'),
          this.$store.dispatch(
            'initBotAudio',
            (window.Audio) ? new Audio() : null,
          ),
        ]);
      })
      .then(() => {
        // This processing block adjusts the LexRunTime client dynamically based on the
        // currently configured region and poolId. Both values by this time should be
        // available in $store.state.
        //
        // A new lexRunTimeClient is constructed targeting Lex in the identified region
        // using credentials built from the identified poolId.
        //
        // The Cognito Identity Pool should be a resource in the identified region.

        // Check for required config values (region & poolId)
        if (!this.$store.state || !this.$store.state.config) {
          return Promise.reject(new Error('no config found'))
        }
        
        // Ensure cognito config exists
        if (!this.$store.state.config.cognito) {
          console.error('No cognito configuration found in config');
          this.$store.state.config.cognito = { region: 'us-east-1', poolId: '' };
          return Promise.reject(new Error('no cognito configuration found in config'));
        }
        
        const region = this.$store.state.config.region ? this.$store.state.config.region : this.$store.state.config.cognito.region;
        if (!region) {
          return Promise.reject(new Error('no region found in config or config.cognito'))
        }
        const poolId = this.$store.state.config.cognito.poolId;
        if (!poolId) {
          return Promise.reject(new Error('no cognito.poolId found in config'))
        }

        const AWSConfigConstructor = (window.AWS && window.AWS.Config) ?
          window.AWS.Config :
          AWSConfig;

        const CognitoConstructor =
          (window.AWS && window.AWS.CognitoIdentityCredentials) ?
            window.AWS.CognitoIdentityCredentials :
            CognitoIdentityCredentials;

        const LexRuntimeConstructor = (window.AWS && window.AWS.LexRuntime) ?
          window.AWS.LexRuntime :
          LexRuntime;

        const LexRuntimeConstructorV2 = (window.AWS && window.AWS.LexRuntimeV2) ?
          window.AWS.LexRuntimeV2 :
          LexRuntimeV2;

        const credentials = new CognitoConstructor(
          { IdentityPoolId: poolId },
          { region: region },
        );

        const awsConfig = new AWSConfigConstructor({
          region: region,
          credentials,
        });

        // Ensure $lexWebUi exists before assigning properties
        if (!this.$lexWebUi) {
          this.$lexWebUi = {};
        }
        
        this.$lexWebUi.lexRuntimeClient = new LexRuntimeConstructor(awsConfig);
        this.$lexWebUi.lexRuntimeV2Client = new LexRuntimeConstructorV2(awsConfig);
        /* eslint-disable no-console */
        console.log(`lexRuntimeV2Client : ${JSON.stringify(this.$lexWebUi.lexRuntimeV2Client)}`);

        const promises = [
          this.$store.dispatch('initMessageList'),
          this.$store.dispatch('initPollyClient', this.$lexWebUi && this.$lexWebUi.pollyClient ? this.$lexWebUi.pollyClient : null),
          this.$store.dispatch('initLexClient', {
            v1client: this.$lexWebUi && this.$lexWebUi.lexRuntimeClient ? this.$lexWebUi.lexRuntimeClient : null, 
            v2client: this.$lexWebUi && this.$lexWebUi.lexRuntimeV2Client ? this.$lexWebUi.lexRuntimeV2Client : null,
          }),
        ];
        console.info('CONFIG : ', this.$store.state.config);
        if (this.$store.state && this.$store.state.config &&
            this.$store.state.config.ui.enableLiveChat) {
          promises.push(this.$store.dispatch('initLiveChat'));
        }
        return Promise.all(promises);
      })
      .then(() => {
        document.title = this.$store.state.config.ui.pageTitle;
      })
      .then(() => (
        (this.$store.state.isRunningEmbedded) ?
          this.$store.dispatch(
            'sendMessageToParentWindow',
            { event: 'ready' },
          ) :
          Promise.resolve()
      ))
      .then(() => {
        if (this.$store.state.config.ui.saveHistory === true) {
          this.$store.subscribe((mutation, state) => {
            sessionStorage.setItem('store', JSON.stringify(state));
          });
        }
      })
      .then(() => {
        console.info(
          'successfully initialized lex web ui version: ',
          this.$store.state.version,
        );

        
        // Send initial utterance if defined
        if (!this.$store.state.config.iframe.shouldLoadIframeMinimized) {
          setTimeout(() => this.$store.dispatch('sendInitialUtterance'), 500);
          this.$store.commit('setInitialUtteranceSent', true);
        }
      })
      .catch((error) => {
        console.error('could not initialize application while mounting:', error);
      });
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize, { passive: true });
    }
  },
  mounted() {
    if (!this.$store.state.isRunningEmbedded) {
      this.$store.dispatch(
        'sendMessageToParentWindow',
        { event: 'requestTokens' },
      );
      this.setFocusIfEnabled();
    }
    this.onResize();
    window.addEventListener('resize', this.onResize, { passive: true });
  },
  methods: {
    onResize() {
      const { innerWidth } = window;
      this.setToolbarHeigthClassSuffix(innerWidth);
    },
    setToolbarHeigthClassSuffix(innerWidth) {
      let suffix = '';
      if (innerWidth < 600) {
        suffix = 'sm';
      } else if (innerWidth < 1264) {
        suffix = 'md';
      } else {
        suffix = 'lg';
      }
      this.toolbarHeightClassSuffix = suffix;
    },
    toggleMinimizeUi() {
      // Toggle the UI minimized state
      this.$store.commit('toggleIsUiMinimized');
      
      // Reset maximized state when minimizing
      if (this.$store.state.isUiMinimized) {
        this.isMaximized = false;
      }
      
      // Force a UI refresh after state change
      this.$nextTick(() => {
        // Trigger a resize event to ensure components update
        window.dispatchEvent(new Event('resize'));
        
        // If maximizing, focus the input field
        if (!this.$store.state.isUiMinimized && this.$refs.InputContainer) {
          this.$refs.InputContainer.setInputTextFieldFocus();
        }
      });
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized;
      // Force re-render of input container when toggling maximize
      this.$nextTick(() => {
        if (this.$refs.InputContainer) {
          this.$refs.InputContainer.setInputTextFieldFocus();
        }
      });
    },
    loginConfirmed(evt) {
      this.$store.commit('setIsLoggedIn', true);
      if (evt.detail && evt.detail.data) {
        this.$store.commit('setTokens', evt.detail.data);
      } else if (evt.data && evt.data.data) {
        this.$store.commit('setTokens', evt.data.data);
      }
    },
    logoutConfirmed() {
      this.$store.commit('setIsLoggedIn', false);
      this.$store.commit('setTokens', {
        idtokenjwt: '',
        accesstokenjwt: '',
        refreshtoken: '',
      });
    },
    handleRequestLogin() {
      console.info('request login');
      if (this.$store.state.isRunningEmbedded) {
        this.$store.dispatch(
          'sendMessageToParentWindow',
          { event: 'requestLogin' },
        );
      } else {
        this.$store.dispatch(
          'sendMessageToParentWindow',
          { event: 'requestLogin' },
        );
      }
    },
    handleRequestLogout() {
      console.info('request logout');
      if (this.$store.state.isRunningEmbedded) {
        this.$store.dispatch(
          'sendMessageToParentWindow',
          { event: 'requestLogout' },
        );
      } else {
        this.$store.dispatch(
          'sendMessageToParentWindow',
          { event: 'requestLogout' },
        );
      }
    },
    handleRequestLiveChat() {
      console.info('handleRequestLiveChat');
      this.$store.dispatch('requestLiveChat');
    },
    handleEndLiveChat() {
      console.info('LexWeb: handleEndLiveChat');
      try {
        this.$store.dispatch('requestLiveChatEnd');
      } catch (error) {
        console.error(`error requesting disconnect ${error}`);
        this.$store.dispatch('pushLiveChatMessage', {
          type: 'agent',
          text: this.$store.state.config.connect.chatEndedMessage,
        });
        this.$store.dispatch('liveChatSessionEnded');
      }
      
      // Add chat ended message immediately
      this.$store.commit('pushMessage', {
        type: 'bot',
        text: 'La chat è terminata.',
        id: this.$store.state.messages.length
      });
    },
    // messages from parent
    messageHandler(evt) {
      const messageType = 'button'; // Always use button type to prevent empty bubbles
      // security check
      if (evt.origin !== this.$store.state.config.ui.parentOrigin) {
        console.warn('ignoring event - invalid origin:', evt.origin);
        return;
      }
      if (!evt.ports || !Array.isArray(evt.ports) || !evt.ports.length) {
        console.warn('postMessage not sent over MessageChannel', evt);
        return;
      }
      switch (evt.data.event) {
        case 'ping':
          console.info('pong - ping received from parent');
          evt.ports[0].postMessage({
            event: 'resolve',
            type: evt.data.event,
          });
          this.setFocusIfEnabled();
          break;
        // received when the parent page has loaded the iframe
        case 'parentReady':
          evt.ports[0].postMessage({ event: 'resolve', type: evt.data.event });
          break;
        case 'toggleMinimizeUi':
          this.$store.commit('toggleIsUiMinimized');
          evt.ports[0].postMessage({
            event: 'resolve', type: evt.data.event,
          });
          break;
        case 'postText':
          if (!evt.data.message) {
            evt.ports[0].postMessage({
              event: 'reject',
              type: evt.data.event,
              error: 'missing message field',
            });
            return;
          }
          this.$store.dispatch(
            'postTextMessage',
            { type: evt.data.messageType ? evt.data.messageType : messageType, text: evt.data.message },
          )
            .then(() => evt.ports[0].postMessage({
              event: 'resolve', type: evt.data.event,
            }));
          break;
        case 'deleteSession':
          this.$store.dispatch('deleteSession')
            .then(() => evt.ports[0].postMessage({
              event: 'resolve', type: evt.data.event,
            }));
          break;
        case 'startNewSession':
          this.$store.dispatch('startNewSession')
            .then(() => evt.ports[0].postMessage({
              event: 'resolve', type: evt.data.event,
            }));
          break;
        case 'setSessionAttribute':
          console.log(`From LexWeb: ${JSON.stringify(evt.data,null,2)}`);
          this.$store.dispatch(
            'setSessionAttribute',
            { key: evt.data.key, value: evt.data.value },
          )
            .then(() => evt.ports[0].postMessage({
              event: 'resolve', type: evt.data.event,
            }));
          break;
        case 'confirmLogin':
          this.loginConfirmed(evt);
          this.userNameValue = this.userName();
          break;
        case 'confirmLogout':
          this.logoutConfirmed();
          break;
        default:
          console.warn('unknown message in messageHandler', evt);
          break;
      }
    },
    componentMessageHandler(evt) {
      switch (evt.detail.event) {
        case 'confirmLogin':
          this.loginConfirmed(evt);
          this.userNameValue = this.userName();
          break;
        case 'confirmLogout':
          this.logoutConfirmed();
          break;
        case 'ping':
          this.$store.dispatch(
            'sendMessageToParentWindow',
            { event: 'pong' },
          );
          break;
        case 'postText':
          this.$store.dispatch(
            'postTextMessage',
            { type: 'human', text: evt.detail.message },
          );
          break;
        case 'replaceCreds':
          this.$store.dispatch(
            'initCredentials',
            evt.detail.creds,
          );
          break;
        default:
          console.warn('unknown message in componentMessageHandler', evt);
          break;
      }
    },
    userName() {
      return this.$store.getters.userName();
    },
    logRunningMode() {
      if (!this.$store.state.isRunningEmbedded) {
        console.info('running in standalone mode');
        return;
      }

      console.info(
        'running in embedded mode from URL: ',
        document.location.href,
      );
      console.info('referrer (possible parent) URL: ', document.referrer);
      console.info(
        'config parentOrigin:',
        this.$store.state.config.ui.parentOrigin,
      );
      if (!document.referrer
        .startsWith(this.$store.state.config.ui.parentOrigin)
      ) {
        console.warn(
          'referrer origin: [%s] does not match configured parent origin: [%s]',
          document.referrer, this.$store.state.config.ui.parentOrigin,
        );
      }
    },
    initConfig() {
      if (this.$store.state.config && this.$store.state.config.urlQueryParams && 
          this.$store.state.config.urlQueryParams.lexWebUiEmbed !== 'true') {
        document.addEventListener('lexwebuicomponent', this.componentMessageHandler, false);
        this.$store.commit('setIsRunningEmbedded', false);
        this.$store.commit('setAwsCredsProvider', 'cognito');
      } else {
        window.addEventListener('message', this.messageHandler, false);
        this.$store.commit('setIsRunningEmbedded', true);
        this.$store.commit('setAwsCredsProvider', 'parentWindow');
      }

      // Initialize $lexWebUi if it doesn't exist
      if (!this.$lexWebUi) {
        console.warn('$lexWebUi is undefined, initializing with default values');
        this.$lexWebUi = {
          config: {},
          awsConfig: {
            credentials: null
          },
          lexRuntimeClient: null,
          lexRuntimeV2Client: null,
          pollyClient: null
        };
      } else if (!this.$lexWebUi.config) {
        console.warn('$lexWebUi.config is undefined, initializing with empty object');
        this.$lexWebUi.config = {};
      }

      if (!this.$lexWebUi.awsConfig) {
        console.warn('$lexWebUi.awsConfig is undefined, initializing with default values');
        this.$lexWebUi.awsConfig = {
          credentials: null
        };
      }

      // get config
      return this.$store.dispatch('initConfig', this.$lexWebUi.config)
        .then(() => this.$store.dispatch('getConfigFromParent'))
        // avoid merging an empty config
        .then(config => (
          (Object.keys(config).length) ?
            this.$store.dispatch('initConfig', config) : Promise.resolve()
        ))
        .then(() => {
          this.setFocusIfEnabled();
          this.logRunningMode();
        });
    },
    setFocusIfEnabled() {
      // Check if the reference exists before trying to access its methods
      if (this.$store.state.config.ui.directFocusToBotInput && 
          this.$refs.InputContainer) {
        this.$refs.InputContainer.setInputTextFieldFocus();
      }
    },
    clearChat() {
      // Completely reset the session state to start fresh
      this.$store.dispatch('startNewSession')
        .then(() => {
          // Reset all state variables that might affect button behavior
          this.$store.state.hasButtons = false;
          
          // Directly commit the clearMessages mutation to remove all messages
          this.$store.commit('clearMessages');
          
          // Reset button disabled state to allow clicking the buttons in the new message
          this.$store.commit('resetButtonDisabledState');
          
          // Force a small delay to ensure all components have updated their state
          setTimeout(() => {
            // Add the default welcome message with device repair options
            this.$store.commit('pushMessage', {
            type: 'bot',
            text: 'Ciao! Benvenuto nel Supporto per la Riparazione dei Dispositivi Amazon. Come posso aiutarti oggi stesso con le tue esigenze di riparazione dei dispositivi Amazon? Che tu abbia bisogno di aiuto per tracciare un ordine, creare una nuova richiesta di assistenza o avere domande sui nostri servizi di riparazione, sono qui per aiutarti.',
        responseCard: {
          version: 1,
          contentType: 'application/vnd.amazonaws.card.generic',
          genericAttachments: [
            // {
            //   buttons: [
            //     { text: 'Kindle', value: 'Kindle' },
            //     { text: 'Echo Speaker', value: 'Echo Speaker' },
            //     { text: 'Fire Tablet', value: 'Fire Tablet' },
            //     { text: 'Fire TV', value: 'Fire TV' },
            //   ]
            // },
            // {
            //   buttons: [
            //     { text: 'Find By Device Name', value: 'Find By Device Name' },
            //     { text: 'Find By Model Number', value: 'Find By Model Number' },
            //     { text: 'Search By Serial Number', value: 'Search By Serial Number' },
            //   ]
            // }
          ]
        }
          });
              
              // Close the menu after clearing the chat
              this.isMenuOpen = false;
              
              // Focus the input field after clearing
              this.$nextTick(() => {
                if (this.$refs.InputContainer) {
                  this.$refs.InputContainer.setInputTextFieldFocus();
                }
              });
            }, 50); // Small delay to ensure state is reset
        });
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    goToPrevious() {
      // Handle previous button click - go back one step in the conversation and re-enable buttons
      console.log('Previous button clicked - going back one step');
      
      // Reset button disabled state to allow clicking again
      this.$store.commit('resetButtonDisabledState');
      
      // Use the goBackOneStep mutation to remove the last message
      this.$store.commit('goBackOneStep');
      
      // Emit event for any parent components that need to know
      this.$emit('previousClicked', {
        currentMessages: this.$store.state.messages
      });
    },
    updateConversationCount(messages) {
      // Logic to determine conversation count
      // A new conversation typically starts with a user message followed by a bot response
      
      if (!messages || messages.length === 0) return;
      
      // Simple implementation: count sequences of user-bot exchanges
      let conversationCount = 0;
      let lastMessageType = null;
      
      for (let i = 0; i < messages.length; i++) {
        const currentType = messages[i].type;
        
        // If we see a transition from bot to user, it could indicate a new conversation segment
        if (lastMessageType === 'bot' && currentType === 'human') {
          conversationCount++;
        }
        
        lastMessageType = currentType;
      }
      
      // Ensure we count the initial conversation too
      if (messages.length > 0 && conversationCount === 0) {
        conversationCount = 1;
      }
      
      this.conversationCount = conversationCount;
      console.log('Conversation count:', this.conversationCount);
    }
  },
};
</script>

<style scoped>
/*
The Vuetify toolbar height is based on screen width breakpoints
The toolbar can be 48px, 56px and 64px.
It is fixed to 48px when using 'dense'

The message list is placed between the toolbar at the top and input
container on the bottom. Both the toolbar and the input-container
dynamically change height based on width breakpoints.
So we duplicate the height and substract it from the total height
of the message list to make it fit between the toolbar and input container

NOTE: not using var() for different heights due to IE11 compatibility
*/

/* Concierge Assistant Styling */
#lex-web {
  background-color: transparent;
  font-family: 'Roboto', sans-serif;
  position: relative;
}

#lex-web.ui-minimized {
  background: transparent;
}

/* Floating chat button styling */
.floating-chat-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
}

.chat-toggle-btn {
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 0 20px;
  background-color: #0e365c !important;
  color: white !important;
}

.chat-toggle-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  background-color: #0f4070 !important;
}

/* Chat container styling */
.chat-container {
  position: fixed;
  width: 400px;
  height: 600px;
  max-height: 90vh;
  bottom: 20px;
  right: 20px;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.3s ease, opacity 0.3s ease;
  z-index: 1000;
}

.chat-content-area {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: calc(100% - 60px - 70px); /* Subtract header height and input container height */
  overflow: hidden;
  position: relative;
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

.message-list-container {
  padding: 0;
  background-color: #fefefe;
  height: 100% !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
}

.concierge-messages {
  height: 100%;
  width: 100%;
  max-width: 100%;
  transition: opacity 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.concierge-input {
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  padding: 8px 12px;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  z-index: 10;
  transition: all 0.3s ease;
  width: 100%;
  height: 70px; /* Fixed height for input container */
}

.input-hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(20px);
  transition: all 0.3s ease;
}

.input-visible {
  opacity: 1;
  pointer-events: auto;
  transform: translateY(0);
  transition: all 0.3s ease;
}

/* Positioning for minimized/maximized states */
.chat-minimized {
  transform: translateY(calc(100% + 20px));
  opacity: 0;
  pointer-events: none;
  transition: transform 0.4s ease, opacity 0.3s ease;
}

.chat-maximized {
  transform: translateY(0);
  opacity: 1;
  pointer-events: auto;
  transition: transform 0.4s ease, opacity 0.3s ease;
}

/* When in fullscreen mode */
.chat-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  transform: none !important;
  width: 100vw !important;
  height: 100vh !important;
  max-width: none !important;
  max-height: none !important;
  border-radius: 0 !important;
  z-index: 1001 !important;
  opacity: 1 !important;
  pointer-events: auto !important;
  box-shadow: none !important;
  overflow:scroll!important;
}

.message-list-container.fullscreen-mode {
  height: calc(100vh - 180px) !important; /* Adjusted for header + input container */
}

.main-hidden {
  opacity: 0;
  pointer-events: none;
}

.messages-hidden {
  opacity: 0;
  pointer-events: none;
}

/* Chat header styling */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background-color: #143659;
  color: #ffffff;
  height: 60px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.header-left {
  display: flex;
  align-items: center;
}

.menu-dropdown {
  position: relative;
  display: inline-block;
}

.menu-btn {
  border-radius: 50%;
  min-width: 36px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.2);
  transition: background-color 0.2s ease;
  border: none;
  cursor: pointer;
  color: white;
  font-size: 16px;
  margin-right: 8px;
}

.menu-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

/* Previous button styling */
.prev-btn {
  border-radius: 50%;
  min-width: 36px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.2);
  transition: background-color 0.2s ease;
  border: none;
  cursor: pointer;
  color: white;
  font-size: 16px;
  margin-right: 12px;
}

.prev-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.prev-icon {
  font-size: 18px;
  line-height: 1;
}

.chat-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.5px;
}

.chat-controls {
  display: flex;
  gap: 8px;
}

.control-btn {
  border-radius: 50%;
  min-width: 36px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.2);
  transition: background-color 0.2s ease;
  border: none;
  cursor: pointer;
  color: white;
  font-size: 16px;
  margin-left: 8px;
}

.control-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.control-btn .icon {
  font-size: 14px;
  line-height: 1;
}

.minimize-btn .icon {
  margin-top: -8px;
}

.close-btn:hover {
  background-color: rgba(255, 0, 0, 0.3);
}

.icon-chat {
  display: none;
}

/* End Chat Button Styling */
.end-chat-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 20px;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-weight: 500;
  margin-right: 70px!important;
}

.end-chat-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-1px);
}

.end-chat-text {
  font-size: 14px;
  font-weight: 500;
}

.end-chat-icon {
  font-size: 16px;
}

.dropdown-content {
  position: absolute;
  top: 100%;
  left: 0;
  background-color: white;
  min-width: 180px;
  box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
  z-index: 20;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 5px;
}

.dropdown-item {
  color: #143659;
  padding: 12px 16px;
  text-decoration: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
}

.dropdown-item:hover {
  background-color: #f7f9fc;
  transform: translateX(3px);
}

.dropdown-icon {
  margin-right: 8px;
  font-size: 18px;
}

.dropdown-text {
  font-size: 14px;
}

.ask-us-icon {
  width: 80px;
  height: 80px;
}

.chat-toggle-btn.icon-only {
  min-width: auto !important;
  padding: 0 !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.chat-toggle-btn.icon-only:hover {
  transform: translateY(-3px);
  background-color: transparent !important;
}

@media (max-width: 600px) {
  .chat-container {
    width: 100%;
    height: 80%;
    bottom: 0;
    right: 0;
    border-radius: 16px 16px 0 0;
  }
  
  .chat-minimized {
    transform: translateY(100%);
  }
  
  .floating-chat-button {
    bottom: 10px;
    right: 10px;
  }
}

html { 
  font-size: 14px !important; 
}

.v-application {
  line-height: 1.5;
  letter-spacing: 0.3px;
}

.v-btn {
  letter-spacing: 0.5px;
  text-transform: none;
  font-weight: 500;
}

.v-toolbar__title {
  font-weight: 500;
  letter-spacing: 0.5px;
}

.fade-transition-enter-active, .fade-transition-leave-active {
  transition: opacity 0.3s ease;
}

.fade-transition-enter, .fade-transition-leave-to {
  opacity: 0;
}
</style>
